/**
 * Pure helpers for the PA performance dashboard: range presets, the change
 * between two counts, cumulative trend series and table sorting. Nothing here
 * touches the network or the clock (the caller passes "today").
 */
import type { PaPerformanceAdvocate, PaPerformanceTrendRow, PerfMeasures, PerfPeriodKey } from '../../services/reportingApi';

export const ZONE = 'America/New_York';

export const ZERO_MEASURES: PerfMeasures = {
  leads: 0, workable: 0, one_ear_only: 0, liability_missing: 0, leads_source_reported: 0,
  sales: 0, sales_reversed: 0, sales_not_standing: 0, sales_net: 0, sale_value: 0, sale_value_net: 0, amount_pending: 0,
  ears_two: 0, ears_one: 0, ears_unknown: 0,
};

/* ── Dates (all America/New_York calendar days, as YYYY-MM-DD strings) ─────── */

export function todayInZone(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const part = (type: string) => parts.find((item) => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function isDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

export function addDays(date: string, by: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + by);
  return iso(d);
}

/** Whole days from `a` to `b` (b - a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / 86400000);
}

export type RangePreset = 'this_month' | 'last_month' | 'last_30' | 'quarter';

export const PRESET_LABEL: Record<RangePreset, string> = {
  this_month: 'This month',
  last_month: 'Last month',
  last_30: 'Last 30 days',
  quarter: 'Quarter to date',
};

export const PRESETS: RangePreset[] = ['this_month', 'last_month', 'last_30', 'quarter'];

/** The From/To a preset stands for, given today's New York date. */
export function presetRange(preset: RangePreset, today: string): { from: string; to: string } {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const pad = (n: number) => String(n).padStart(2, '0');
  switch (preset) {
    case 'this_month':
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case 'last_month': {
      const first = new Date(Date.UTC(year, month - 2, 1));
      const last = new Date(Date.UTC(year, month - 1, 0));
      return { from: iso(first), to: iso(last) };
    }
    case 'last_30':
      return { from: addDays(today, -29), to: today };
    case 'quarter': {
      const startMonth = Math.floor((month - 1) / 3) * 3 + 1;
      return { from: `${year}-${pad(startMonth)}-01`, to: today };
    }
    default:
      return { from: today, to: today };
  }
}

/** Which preset (if any) the current From/To exactly matches. */
export function matchingPreset(from: string, to: string, today: string): RangePreset | null {
  return PRESETS.find((p) => {
    const r = presetRange(p, today);
    return r.from === from && r.to === to;
  }) || null;
}

/** Why a From/To cannot be sent, or null when it can. */
export function rangeProblem(from: string, to: string): string | null {
  if (!isDate(from) || !isDate(to)) return 'Choose valid dates for both From and To.';
  if (from > to) return 'The start date must be on or before the end date.';
  return null;
}

/** "Oct 1" or, with a year, "Oct 1, 2026". UTC-formatted so no zone moves the day. */
export function dayWords(date: string, withYear = false): string {
  return new Intl.DateTimeFormat('en-US', withYear
    ? { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }
    : { timeZone: 'UTC', month: 'short', day: 'numeric' }).format(new Date(`${date}T00:00:00Z`));
}

/** "Oct 1 – Oct 19, 2026" (the year shown once, on the end, or on both when they differ). */
export function rangeWords(from: string, to: string): string {
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  return `${dayWords(from, !sameYear)} – ${dayWords(to, true)}`;
}

export function monthName(date: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long' }).format(new Date(`${date}T00:00:00Z`));
}

export const dayCount = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;

/* ── Change between two counts ─────────────────────────────────────────────── */

export type DeltaDirection = 'up' | 'down' | 'flat' | 'new';

export interface Delta {
  direction: DeltaDirection;
  /** current - prior */
  change: number;
  /** Whole-number percent, null when it cannot be stated (prior 0). */
  percent: number | null;
  /** "+5", "−3", "0" */
  changeText: string;
  /** "+25%", "−10%", "n/a", "" */
  percentText: string;
  /** The badge words: "+5 · +25%", "+4 · new", "No change". */
  text: string;
  /** For assistive tech: a full sentence. */
  sentence: string;
}

const signed = (n: number) => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : '0');

export function computeDelta(current: number, prior: number): Delta {
  const change = current - prior;
  if (prior === 0 && current === 0) {
    return { direction: 'flat', change: 0, percent: null, changeText: '0', percentText: '', text: 'No change', sentence: 'No change from the comparison period.' };
  }
  if (prior === 0) {
    return {
      direction: 'new', change, percent: null, changeText: signed(change), percentText: 'n/a',
      text: `${signed(change)} · new`, sentence: `${signed(change)}, new: the comparison period had none (percent not applicable).`,
    };
  }
  const percent = Math.round((change / prior) * 100);
  if (change === 0) {
    return { direction: 'flat', change: 0, percent: 0, changeText: '0', percentText: '0%', text: 'No change', sentence: 'No change from the comparison period.' };
  }
  const percentText = `${percent > 0 ? '+' : percent < 0 ? '−' : ''}${Math.abs(percent)}%`;
  return {
    direction: change > 0 ? 'up' : 'down', change, percent, changeText: signed(change), percentText,
    text: `${signed(change)} · ${percentText}`,
    sentence: `${change > 0 ? 'Up' : 'Down'} ${Math.abs(change)} (${percentText}) from the comparison period.`,
  };
}

/* ── Money ─────────────────────────────────────────────────────────────────── */

export function money(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'Not recorded';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

/* ── Trend: cumulative by day index ────────────────────────────────────────── */

export type TrendMeasure = 'leads' | 'workable' | 'sales';
export const MEASURE_LABEL: Record<TrendMeasure, string> = { leads: 'Leads', workable: 'Workable', sales: 'Sales' };

export interface CumulativePoint {
  /** 1-based day of the period. */
  day: number;
  current?: number;
  comparison?: number;
  reference?: number;
}

interface PeriodSpan { from: string; days: number }

/**
 * One row per day index (1 .. longest period). Each period's running total
 * stops at its own last day (a month-to-date period does not draw zeros for
 * days that have not happened). Days with no activity are filled with zero.
 */
export function cumulativeSeries(
  trend: PaPerformanceTrendRow[],
  spans: { current: PeriodSpan; comparison: PeriodSpan; reference: PeriodSpan | null },
  measure: TrendMeasure,
): CumulativePoint[] {
  const keys: PerfPeriodKey[] = ['current', 'comparison', 'reference'];
  const run: Partial<Record<PerfPeriodKey, number[]>> = {};
  keys.forEach((key) => {
    const span = spans[key];
    if (!span) return;
    const perDay = new Array<number>(span.days).fill(0);
    trend.filter((r) => r.period === key).forEach((r) => {
      const idx = daysBetween(span.from, r.date);
      if (idx >= 0 && idx < span.days) perDay[idx] += Number(r[measure]) || 0;
    });
    let total = 0;
    run[key] = perDay.map((v) => { total += v; return total; });
  });
  const longest = Math.max(0, ...keys.map((k) => run[k]?.length || 0));
  return Array.from({ length: longest }, (_, i) => ({
    day: i + 1,
    current: run.current?.[i],
    comparison: run.comparison?.[i],
    reference: run.reference?.[i],
  }));
}

/* ── Per-PA table ──────────────────────────────────────────────────────────── */

export type SortKey = 'name' | 'leads' | 'workable' | 'sales' | 'sales_net' | 'sale_value' | 'sales_reversed';
export type SortDir = 'asc' | 'desc';

const sortValue = (a: PaPerformanceAdvocate, key: SortKey): number | string => {
  if (key === 'name') return a.advocate.toLowerCase();
  return a.current[key];
};

/** Sorted rows; the "No PA credited" row (no user id) is always last, whichever way the column runs. */
export function sortAdvocates(rows: PaPerformanceAdvocate[], key: SortKey, dir: SortDir): PaPerformanceAdvocate[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const aNone = a.advocate_user_id === null;
    const bNone = b.advocate_user_id === null;
    if (aNone !== bNone) return aNone ? 1 : -1;
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    let c = 0;
    if (typeof av === 'string' || typeof bv === 'string') c = String(av).localeCompare(String(bv));
    else c = av - bv;
    if (c === 0) c = a.advocate.localeCompare(b.advocate);
    return c * sign;
  });
}

export const advocateKey = (a: PaPerformanceAdvocate) => a.advocate_user_id ?? '__none__';
